import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Clock, RotateCcw, Plus, X } from 'lucide-react';
import { versionsApi, type Version } from '../api/versions';
import { toast } from './Toast';
import { formatRelativeTime } from '../lib/utils';

interface VersionHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docId: string;
}

export function VersionHistory({ open, onOpenChange, docId }: VersionHistoryProps) {
  const queryClient = useQueryClient();

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['versions', docId],
    queryFn: () => versionsApi.list(docId),
    enabled: open && !!docId,
  });

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [restoreTarget, setRestoreTarget] = useState<Version | null>(null);

  const createMutation = useMutation({
    mutationFn: () => versionsApi.create(docId, { note: versionNote.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', docId] });
      toast.success('版本已创建');
      setShowCreateDialog(false);
      setVersionNote('');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '创建版本失败');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => versionsApi.restore(docId, restoreTarget!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', docId] });
      toast.success('已回滚到指定版本，正在刷新…');
      setRestoreTarget(null);
      // Y.applyUpdate 是合并语义，无法真正"替换"本地 Yjs doc。
      // 只有整页 reload 让前端从 DO 重新 sync，才能拿到干净的快照状态。
      setTimeout(() => window.location.reload(), 600);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '回滚失败');
    },
  });

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[560px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-text-secondary" />
                <Dialog.Title className="text-lg font-semibold">版本历史</Dialog.Title>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateDialog(true)}
                  className="btn btn-secondary btn-sm"
                >
                  <Plus className="h-3.5 w-3.5" /> 创建命名版本
                </button>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle"
                    aria-label="关闭"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            <div className="mt-5">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-16 animate-shimmer rounded-lg bg-bg-subtle" />
                  ))}
                </div>
              ) : versions.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center">
                  <Clock className="h-8 w-8 text-text-tertiary" />
                  <p className="mt-2 text-sm text-text-tertiary">暂无版本记录</p>
                  <p className="text-xs text-text-tertiary">保存文档后将自动创建版本</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      className="flex items-center gap-4 rounded-lg border border-border p-4"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-50 text-accent">
                        <Clock className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            v{version.versionNumber}
                          </span>
                          {version.note && (
                            <span className="text-sm text-text-secondary">
                              — {version.note}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-3 text-xs text-text-tertiary">
                          <span>{formatRelativeTime(version.createdAt)}</span>
                          <span>{version.createdByNickname || version.createdBy}</span>
                          <span>{formatFileSize(version.sizeBytes)}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setRestoreTarget(version)}
                        className="btn btn-ghost btn-sm"
                        title="回滚到此版本"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 创建命名版本弹窗 */}
      <Dialog.Root open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[420px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold">创建命名版本</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-secondary">
              为当前文档状态创建一个命名版本，方便后续回滚。
            </Dialog.Description>
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium" htmlFor="version-note">
                版本说明
              </label>
              <textarea
                id="version-note"
                className="input min-h-[80px] resize-none"
                value={versionNote}
                onChange={(e) => setVersionNote(e.target.value)}
                placeholder="例如：完成初稿、修改了结论部分…"
                rows={3}
                autoFocus
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateDialog(false);
                  setVersionNote('');
                }}
                className="btn btn-ghost btn-md"
              >
                取消
              </button>
              <button
                type="button"
                disabled={!versionNote.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
                className="btn btn-primary btn-md"
              >
                {createMutation.isPending ? '创建中…' : '创建版本'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 回滚确认弹窗 */}
      <Dialog.Root
        open={!!restoreTarget}
        onOpenChange={(o) => !o && setRestoreTarget(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[420px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <div className="flex items-start justify-between">
              <Dialog.Title className="text-lg font-semibold">回滚版本</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle"
                  aria-label="关闭"
                >
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="mt-2 text-sm text-text-secondary">
              确定要回滚到{' '}
              <span className="font-medium text-text-primary">
                v{restoreTarget?.versionNumber}
              </span>{' '}
              吗？当前未保存的修改将丢失。
              {restoreTarget?.note && (
                <span className="mt-1 block text-text-tertiary">
                  版本说明：{restoreTarget.note}
                </span>
              )}
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRestoreTarget(null)}
                className="btn btn-ghost btn-md"
              >
                取消
              </button>
              <button
                type="button"
                disabled={restoreMutation.isPending}
                onClick={() => restoreMutation.mutate()}
                className="btn btn-danger btn-md"
              >
                {restoreMutation.isPending ? '回滚中…' : '确认回滚'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}